/**
 * Task Detail view with work instructions and action buttons.
 * Shows SO number (NOT customer name), task info, and start/stop/complete.
 */

import { useState, useEffect, useCallback } from 'react';
import type { ContractorTask } from '../lib/api';
import { StatusBadge } from './StatusBadge';
import { Timer } from './Timer';
import {
  formatEstimateRange,
  formatDuration,
  getLaborBucketLabel,
} from '../lib/format';
import { useStartTask, useStopTask, useCompleteTask } from '../hooks/useTasks';
import { useOfflineSync } from '../hooks/useOfflineSync';

interface TaskDetailProps {
  task: ContractorTask;
  onBack: () => void;
  onTaskUpdated?: () => void;
}

export function TaskDetail({ task, onBack, onTaskUpdated }: TaskDetailProps) {
  const startTask = useStartTask();
  const stopTask = useStopTask();
  const completeTask = useCompleteTask();
  const { status: syncStatus, queueAction } = useOfflineSync();
  const [offlineActionPending, setOfflineActionPending] = useState(false);

  const isInProgress = task.status === 'in_progress';
  const isReady = task.status === 'ready';
  const isComplete = task.status === 'complete';
  const canStart = isReady;
  const canStop = isInProgress;
  const canComplete = isInProgress || isReady;

  const isLoading =
    startTask.isPending ||
    stopTask.isPending ||
    completeTask.isPending ||
    offlineActionPending;

  const handleStart = useCallback(async () => {
    if (!syncStatus.isOnline) {
      // Queue for offline sync
      setOfflineActionPending(true);
      try {
        await queueAction(task.id, 'start');
        // Show optimistic UI update indication
        onTaskUpdated?.();
      } finally {
        setOfflineActionPending(false);
      }
      return;
    }

    try {
      await startTask.mutateAsync(task.id);
      onTaskUpdated?.();
    } catch {
      // Error handling is done via mutation state
    }
  }, [startTask, task.id, onTaskUpdated, syncStatus.isOnline, queueAction]);

  const handleStop = useCallback(async () => {
    if (!syncStatus.isOnline) {
      setOfflineActionPending(true);
      try {
        await queueAction(task.id, 'stop');
        onTaskUpdated?.();
      } finally {
        setOfflineActionPending(false);
      }
      return;
    }

    try {
      await stopTask.mutateAsync(task.id);
      onTaskUpdated?.();
    } catch {
      // Error handling is done via mutation state
    }
  }, [stopTask, task.id, onTaskUpdated, syncStatus.isOnline, queueAction]);

  const handleComplete = useCallback(async () => {
    if (!syncStatus.isOnline) {
      setOfflineActionPending(true);
      try {
        await queueAction(task.id, 'complete');
        onTaskUpdated?.();
      } finally {
        setOfflineActionPending(false);
      }
      return;
    }

    try {
      await completeTask.mutateAsync(task.id);
      onTaskUpdated?.();
    } catch {
      // Error handling is done via mutation state
    }
  }, [completeTask, task.id, onTaskUpdated, syncStatus.isOnline, queueAction]);

  const error = startTask.error || stopTask.error || completeTask.error;

  return (
    <div className="flex flex-col h-full">
      {/* Header with back button */}
      <div className="flex-none px-4 py-3 border-b border-rule">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-ink-muted hover:text-ink transition-colors"
        >
          <BackIcon className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Tasks</span>
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Task header */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-xl font-bold text-ink">
                  {task.gpSalesOrderNumber}
                </div>
                <div className="font-mono text-sm text-ink-muted mt-1">
                  {task.partNumber}
                </div>
              </div>
              <StatusBadge status={task.status} size="md" />
            </div>

            <p className="text-base text-ink-muted">{task.partDescription}</p>
          </div>

          {/* Timer section - only show when in progress */}
          {isInProgress && task.activeTimeEntry && (
            <div className="bg-status-in-progress/10 border border-status-in-progress/30 rounded-xl p-4">
              <div className="text-xs text-status-in-progress font-semibold uppercase tracking-wider mb-2">
                Time Elapsed
              </div>
              <Timer startTime={task.activeTimeEntry.startTime} />
            </div>
          )}

          {/* Task info cards */}
          <div className="grid grid-cols-2 gap-3">
            <InfoCard
              icon={<WorkIcon className="w-4 h-4" />}
              label="Work Type"
              value={getLaborBucketLabel(task.laborBucket)}
            />
            <InfoCard
              icon={<ClockIcon className="w-4 h-4" />}
              label="Estimate"
              value={formatEstimateRange(
                task.estimatedMinutesMin,
                task.estimatedMinutesMax
              )}
              mono
            />
            <InfoCard
              icon={<CheckIcon className="w-4 h-4" />}
              label="Time Logged"
              value={formatDuration(task.actualMinutes)}
              mono
            />
            <InfoCard
              icon={<TagIcon className="w-4 h-4" />}
              label="Skill"
              value={task.skillName}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-400">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons - fixed at bottom */}
      {!isComplete && (
        <div className="flex-none p-4 border-t border-rule bg-surface safe-bottom">
          <div className="flex gap-3">
            {canStart && (
              <ActionButton
                onClick={handleStart}
                variant="primary"
                disabled={isLoading}
                loading={startTask.isPending}
                className="flex-1"
              >
                <PlayIcon className="w-5 h-5" />
                Start Task
              </ActionButton>
            )}

            {canStop && (
              <>
                <ActionButton
                  onClick={handleStop}
                  variant="secondary"
                  disabled={isLoading}
                  loading={stopTask.isPending}
                  className="flex-1"
                >
                  <PauseIcon className="w-5 h-5" />
                  Pause
                </ActionButton>

                <ActionButton
                  onClick={handleComplete}
                  variant="success"
                  disabled={isLoading}
                  loading={completeTask.isPending}
                  className="flex-1"
                >
                  <CheckIcon className="w-5 h-5" />
                  Complete
                </ActionButton>
              </>
            )}

            {!canStart && !canStop && canComplete && (
              <ActionButton
                onClick={handleComplete}
                variant="success"
                disabled={isLoading}
                loading={completeTask.isPending}
                className="flex-1"
              >
                <CheckIcon className="w-5 h-5" />
                Mark Complete
              </ActionButton>
            )}
          </div>
        </div>
      )}

      {/* Completed state */}
      {isComplete && (
        <div className="flex-none p-4 border-t border-rule bg-surface safe-bottom">
          <div className="flex items-center justify-center gap-2 py-3 text-status-complete">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="font-semibold">Task Complete</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Info card component
interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}

function InfoCard({ icon, label, value, mono }: InfoCardProps) {
  return (
    <div className="bg-surface-raised border border-rule rounded-lg p-3">
      <div className="flex items-center gap-2 text-ink-subtle mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className={`text-sm text-ink ${mono ? 'font-mono' : 'font-medium'}`}>
        {value}
      </div>
    </div>
  );
}

// Action button component
interface ActionButtonProps {
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'success';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  children: React.ReactNode;
}

function ActionButton({
  onClick,
  variant,
  disabled,
  loading,
  className = '',
  children,
}: ActionButtonProps) {
  const variantStyles = {
    primary: 'bg-accent text-white hover:bg-accent-muted',
    secondary: 'bg-surface-highlight text-ink hover:bg-surface-overlay',
    success: 'bg-status-in-progress text-white hover:bg-status-complete',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl
        font-semibold text-base transition-all duration-150
        ${variantStyles[variant]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.98]'}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface
        ${className}
      `}
    >
      {loading ? <LoadingSpinner /> : children}
    </button>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="w-5 h-5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WorkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path
        d="M2 5h12v8a1 1 0 01-1 1H3a1 1 0 01-1-1V5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5V3a1 1 0 011-1h4a1 1 0 011 1v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 4.5V8l2.5 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path
        d="M3.5 8.5L6.5 11.5L12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path
        d="M2 3a1 1 0 011-1h4.586a1 1 0 01.707.293l5.414 5.414a1 1 0 010 1.414l-4.586 4.586a1 1 0 01-1.414 0L2.293 8.293A1 1 0 012 7.586V3z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="5" cy="5" r="1" fill="currentColor" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l7.108 4.347c1.234.755 1.234 2.531 0 3.286l-7.108 4.347C6.029 16.683 4.5 15.78 4.5 14.347V5.653z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M5.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75A.75.75 0 007.25 3h-1.5zM12.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75a.75.75 0 00-.75-.75h-1.5z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}
